<template>
	<div>
		<v-snackbar class="noPrint" v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<loading-modal v-if="isLoadingPerformanceReview" loading-text="Loading Performance Review..." />

		<div v-if="performanceReview != null">
			<v-snackbar v-model="updatedNotif" auto-height top>
				{{ updatedNotifText }}
				<v-btn color="pink" flat @click="updatedNotif = false">
					Close
				</v-btn>
			</v-snackbar>

			<v-alert v-if="performanceReview.status == 'Complete'" style="margin-left:24px; margin-right: 24px;" class="text-xs-left" outline :value="true" type="success" icon="check">
				This Performance Review has been completed and signed off.
			</v-alert>

			<v-alert v-else-if="performanceReview.status == 'Discussion'" style="margin-left:24px; margin-right: 24px;" class="text-xs-left" outline :value="true" type="info" icon="access_time">
				This Performance Review is scheduled for {{ performanceReview.scheduledDateUTC | formatDate() }}
			</v-alert>

			<v-alert v-else-if="performanceReview.status == 'Pending'" style="margin-left:24px; margin-right: 24px;" class="text-xs-left" outline :value="true" type="warning" icon="access_time">
				{{ pendingWarningText }}
			</v-alert>

			<v-container grid-list-xl fluid>
				<v-layout row wrap>
					<v-flex md8>
						<v-card class="mb-4">
							<v-toolbar flat dense cad>
								<v-toolbar-title class="subheading">Review Detail</v-toolbar-title>
								<v-spacer></v-spacer>
								<v-btn v-if="showDiscussionButton" small flat class="" @click="viewDiscussion"
									><v-icon v-if="performanceReview.status == 'Discussion'" left>mdi-square-edit-outline</v-icon><v-icon v-else left>remove_red_eye</v-icon> Discussion</v-btn
								>
							</v-toolbar>
							<v-divider></v-divider>
							<v-list two-line>
								<v-list-tile inactive>
									<v-list-tile-action>
										<v-avatar size="36px">
											<img :src="getAvatar(performanceReview.employee.id)" alt="avatar" class="avatar-img" />
										</v-avatar>
									</v-list-tile-action>

									<v-list-tile-content>
										<v-list-tile-title>{{ performanceReview.employee.fullName }}</v-list-tile-title>
										<v-list-tile-sub-title>{{ $term('employee', { case: 'title' }) }}</v-list-tile-sub-title>
										<v-list-tile-sub-title v-if="performanceReview.employeeSubmit.status === 'Complete'" style="color:#4caf50;">Submitted <AppDate :date="performanceReview.employeeSubmit.completedDateUTC"/></v-list-tile-sub-title>
										<v-list-tile-sub-title v-else-if="performanceReview.employeeSubmit.status === 'InProgress'"
											><span style="color:#fb8c00;">Partially Complete</span> - Last updated <AppDate :date="performanceReview.employeeSubmit.lastUpdatedDateUTC"
										/></v-list-tile-sub-title>
										<v-list-tile-sub-title v-else>Pending Completion</v-list-tile-sub-title>
									</v-list-tile-content>

									<v-list-tile-action v-if="showViewEmployeeSubmitButton">
										<v-btn @click="viewSubmit(performanceReview.employeeSubmit.id)">View</v-btn>
									</v-list-tile-action>
									<v-list-tile-action v-if="showEditEmployeeSubmitButton">
										<v-btn class="action-btn pa-2" @click="editSubmit(performanceReview.employeeSubmit.id)">{{ employeeFillOutReviewButtonTooltip }}</v-btn>
									</v-list-tile-action>
								</v-list-tile>

								<v-divider inset></v-divider>

								<v-list-tile inactive>
									<v-list-tile-action>
										<v-avatar size="36px">
											<img :src="getAvatar(performanceReview.reviewer.id)" alt="avatar" class="avatar-img" />
										</v-avatar>
									</v-list-tile-action>

									<v-list-tile-content>
										<v-list-tile-title>{{ performanceReview.reviewer.fullName }}</v-list-tile-title>
										<v-list-tile-sub-title>Reviewer</v-list-tile-sub-title>
										<v-list-tile-sub-title v-if="performanceReview.reviewerSubmit.status === 'Complete'" style="color:#4caf50;">Submitted <AppDate :date="performanceReview.reviewerSubmit.completedDateUTC"/></v-list-tile-sub-title>
										<v-list-tile-sub-title v-else-if="performanceReview.reviewerSubmit.status === 'InProgress'"
											><span style="color:#fb8c00;">Partially Complete</span> - Last updated <AppDate :date="performanceReview.reviewerSubmit.lastUpdatedDateUTC"
										/></v-list-tile-sub-title>
										<v-list-tile-sub-title v-else>Pending Completion</v-list-tile-sub-title>
									</v-list-tile-content>

									<v-list-tile-action v-if="showViewReviewerSubmitButton">
										<v-btn @click="viewSubmit(performanceReview.reviewerSubmit.id)">View</v-btn>
									</v-list-tile-action>
									<v-list-tile-action v-if="showEditReviewerSubmitButton">
										<v-btn class="action-btn pa-2" @click="editSubmit(performanceReview.reviewerSubmit.id)">{{ reviewerFillOutReviewButtonTooltip }}</v-btn>
									</v-list-tile-action>
								</v-list-tile>

								<v-divider inset></v-divider>

								<v-list-tile inactive>
									<v-list-tile-action>
										<v-icon color="indigo">date_range</v-icon>
									</v-list-tile-action>

									<v-list-tile-content>
										<v-list-tile-title>{{ performanceReview.periodFromUTC | formatDate('MMM YYYY') }} - {{ performanceReview.periodToUTC | formatDate('MMM YYYY') }}</v-list-tile-title>
										<v-list-tile-sub-title>Review Period</v-list-tile-sub-title>
									</v-list-tile-content>
								</v-list-tile>

								<v-divider inset></v-divider>

								<v-list-tile inactive>
									<v-list-tile-action>
										<v-icon color="indigo">date_range</v-icon>
									</v-list-tile-action>

									<v-list-tile-content>
										<v-list-tile-title v-if="performanceReview.scheduledDateTimeSpecified">
											<v-icon color="#f44336" small v-if="isOverdue" title="Overdue">mdi-alert-circle-outline</v-icon>
											{{ performanceReview.scheduledDateUTC | formatDateTime() }}
										</v-list-tile-title>
										<v-list-tile-title v-else>
											<v-icon color="#f44336" small v-if="isOverdue" title="Overdue">mdi-alert-circle-outline</v-icon>
											{{ performanceReview.scheduledDateUTC | formatDate() }}
										</v-list-tile-title>
										<v-list-tile-sub-title>Scheduled Date & Time<span v-if="!performanceReview.scheduledDateTimeSpecified" style="font-style: italic;"> - No time has been specified</span></v-list-tile-sub-title>
									</v-list-tile-content>

									<v-list-tile-action v-if="canReschedule">
										<v-tooltip bottom>
											<v-btn slot="activator" icon ripple @click="onEditScheduledDateClick">
												<v-icon color="grey lighten-1">edit</v-icon>
											</v-btn>
											<span>Reschedule</span>
										</v-tooltip>
									</v-list-tile-action>
								</v-list-tile>

								<v-divider inset></v-divider>

								<v-list-tile inactive>
									<v-list-tile-action>
										<v-icon color="indigo">location_on</v-icon>
									</v-list-tile-action>

									<v-list-tile-content>
										<v-list-tile-title v-if="performanceReview.meetingLocation == null">{{ meetingLocation }}</v-list-tile-title>
										<v-list-tile-title v-else
											><a href="#" @click="meetingLocationDialog = true">{{ meetingLocation }}</a></v-list-tile-title
										>
										<v-list-tile-sub-title>Meeting Location</v-list-tile-sub-title>
									</v-list-tile-content>

									<v-list-tile-action v-if="canReschedule">
										<v-tooltip bottom>
											<v-btn slot="activator" icon ripple @click="onEditMeetingLocationClick">
												<v-icon color="grey lighten-1">edit</v-icon>
											</v-btn>
											<span>Change Location</span>
										</v-tooltip>
									</v-list-tile-action>
								</v-list-tile>
							</v-list>
						</v-card>
					</v-flex>

					<v-flex md4>
						<v-card>
							<v-toolbar flat dense cad>
								<v-toolbar-title class="subheading">Review Documents</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-divider></v-divider>

							<v-list two-line subheader>
								<v-list-tile v-for="file in performanceReview.files" :key="file.id" avatar>
									<v-list-tile-avatar>
										<v-icon class="blue white--text">assignment</v-icon>
									</v-list-tile-avatar>

									<v-list-tile-content>
										<v-list-tile-title>{{ file.fileName }}</v-list-tile-title>
										<v-list-tile-sub-title>Uploaded <AppDate :date="file.createdDateUTC"/></v-list-tile-sub-title>
									</v-list-tile-content>

									<v-list-tile-action>
										<v-tooltip bottom>
											<v-btn slot="activator" icon ripple @click="downloadFile(file.id, file.fileName)">
												<v-icon color="grey">get_app</v-icon>
											</v-btn>
											<span>Download File</span>
										</v-tooltip>
									</v-list-tile-action>

									<v-list-tile-action>
										<v-tooltip bottom>
											<v-btn slot="activator" icon ripple @click="onDeleteFile(file.id)">
												<v-icon color="grey">delete</v-icon>
											</v-btn>
											<span>Remove File</span>
										</v-tooltip>
									</v-list-tile-action>
								</v-list-tile>
							</v-list>

							<v-card-actions>
								<v-spacer></v-spacer>
								<v-btn flat @click="onUploadFileClick"><v-icon left color="grey">cloud_upload</v-icon>Upload Document</v-btn>
							</v-card-actions>
						</v-card>
					</v-flex>
				</v-layout>

				<meeting-location-modal v-if="performanceReview.meetingLocation != null" :meeting-location="performanceReview.meetingLocation" :dialog="meetingLocationDialog" @closeEditor="meetingLocationDialog = false" />

				<file-upload v-if="uploadFileDialog" :dialog="uploadFileDialog" title="Upload Document" @onSecondaryAction="uploadFileDialog = false" @onPrimaryAction="onUploadFile"> </file-upload>

				<performance-review-schedule
					v-if="scheduleDialog"
					:dialog="scheduleDialog"
					:performance-review="performanceReview"
					:meeting-locations="meetingLocations"
					:is-saving="isSavingReschedule"
					@closeEditor="scheduleDialog = false"
					@save="onReschedule"
				/>

				<v-snackbar v-model="fileUploadedNotif" bottom>
					File Uploaded Successfully
					<v-btn color="pink" flat @click="fileUploadedNotif = false">
						Close
					</v-btn>
				</v-snackbar>

				<v-snackbar v-model="fileDeletedNotif" bottom>
					File Deleted Successfully
					<v-btn color="pink" flat @click="fileDeletedNotif = false">
						Close
					</v-btn>
				</v-snackbar>
			</v-container>
			<v-footer color app height="55">
				<v-spacer></v-spacer>
				<footer-back-btn @click="onBackClick" />
			</v-footer>
		</div>
	</div>
</template>

<script>
	import moment from 'moment';
	import FileUpload from '@/components/FileUpload';
	import { mapState, mapGetters } from 'vuex';
	import typeFriendlyMixin from '@/mixins/typeFriendlyMixin';
	import avatarMixin from '@/mixins/avatarMixin';
	import MeetingLocationModal from '@/components/settings/MeetingLocationModal';
	import PerformanceReviewSchedule from '@/components/performanceReview/PerformanceReviewSchedule.vue';
	import LoadingModal from '@/components/LoadingModal';

	export default {
		components: {
			FileUpload,
			MeetingLocationModal,
			PerformanceReviewSchedule,
			LoadingModal
		},
		props: {
			id: {
				required: true
			}
		},
		data() {
			return {
				uploadFileDialog: false,
				fileUploadedNotif: false,
				fileDeletedNotif: false,
				meetingLocationDialog: false,
				scheduleDialog: false,
				updatedNotif: false,
				updatedNotifText: null,
				isSavingReschedule: false,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				performanceReview: null,
				isLoadingPerformanceReview: false
			};
		},
		async created() {
			this.$store.dispatch('setPageTitle', 'Performance Review');

			await this.loadPerformanceReview();
		},
		mixins: [typeFriendlyMixin, avatarMixin],
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				meetingLocations: 'meetingLocations/meetingLocations'
			}),
			...mapState({
				tenantId: state => state.tenantId
			}),
			showDiscussionButton() {
				return this.loggedInEmployee.isAdmin || this.performanceReview.status == 'Complete' || this.performanceReview.reviewer.id == this.loggedInEmployee.id;
			},
			canReschedule() {
				return !this.isReviewComplete && (this.loggedInEmployee.isAdmin || this.performanceReview.reviewer.id == this.loggedInEmployee.id);
			},
			pendingWarningText() {
				let party = 'both parties';
				if (this.performanceReview.employeeSubmit.status == 'Complete' && (this.performanceReview.reviewerSubmit.status == 'Pending' || this.performanceReview.reviewerSubmit.status == 'InProgress')) {
					party = this.performanceReview.reviewer.fullName;
				} else if (this.performanceReview.reviewerSubmit.status == 'Complete' && (this.performanceReview.employeeSubmit.status == 'Pending' || this.performanceReview.employeeSubmit.status == 'InProgress')) {
					party = this.performanceReview.employee.fullName;
				}

				return `Awaiting ${party} to fill out their part of the Performance Review`;
			},
			meetingLocation() {
				if (this.performanceReview.meetingLocation != null) {
					return this.performanceReview.meetingLocation.name;
				} else {
					return 'N/A';
				}
			},
			isReviewComplete() {
				return this.performanceReview.status === 'Complete';
			},
			isEmployeeSubmitComplete() {
				return this.performanceReview.employeeSubmit.status === 'Complete';
			},
			isReviewerSubmitComplete() {
				return this.performanceReview.reviewerSubmit.status === 'Complete';
			},
			isLoggedInEmployee() {
				return this.performanceReview.employee.id == this.loggedInEmployee.id;
			},
			isLoggedInReviewer() {
				return this.performanceReview.reviewer.id == this.loggedInEmployee.id;
			},
			showViewEmployeeSubmitButton() {
				return (this.isLoggedInReviewer && this.isEmployeeSubmitComplete && !this.isLoggedInEmployee) || (this.isLoggedInEmployee && (this.isReviewComplete || this.discussionStarted));
			},
			showEditEmployeeSubmitButton() {
				return this.isLoggedInEmployee && !this.discussionStarted && !this.isReviewComplete;
			},
			showViewReviewerSubmitButton() {
				return this.isLoggedInReviewer && (this.isReviewComplete || this.discussionStarted);
			},
			showEditReviewerSubmitButton() {
				return this.isLoggedInReviewer && !this.discussionStarted && !this.isReviewComplete;
			},
			employeeFillOutReviewButtonTooltip() {
				return this.isEmployeeSubmitComplete ? 'Change my answers' : 'Fill out Review';
			},
			reviewerFillOutReviewButtonTooltip() {
				return this.isReviewerSubmitComplete ? 'Change my answers' : 'Fill out Review';
			},
			isInPast() {
				return moment.utc(this.performanceReview.scheduledDateUTC).isBefore(moment.utc());
			},
			isOverdue() {
				return !this.isReviewComplete && this.isInPast;
			},
			discussionStarted() {
				return this.performanceReview.discussion.status != 'Pending';
			}
		},
		methods: {
			viewSubmit(submitId) {
				this.$router.push(`/pr/${this.id}/submit/${submitId}`);
			},
			editSubmit(submitId) {
				this.$router.push(`/pr/${this.id}/submit/${submitId}/edit`);
			},
			onUploadFileClick() {
				this.uploadFileDialog = true;
			},
			onUploadFile(formData) {
				this.$store
					.dispatch('performanceReviews/uploadDocument', {
						performanceReviewId: this.id,
						document: formData
					})
					.then(
						response => {
							this.loadPerformanceReview();
							this.uploadFileDialog = false;
							this.fileUploadedNotif = true;
						},
						error => {
							console.error('Error uploading document');
						}
					);
			},
			onDeleteFile(id) {
				if (confirm('Are you sure you want to delete this document?')) {
					this.$store
						.dispatch('performanceReviews/deleteDocument', {
							performanceReviewId: this.id,
							id: id
						})
						.then(
							response => {
								this.loadPerformanceReview();
								this.fileDeletedNotif = true;
							},
							error => {
								console.error('Error deleting document');
							}
						);
				}
			},
			async loadPerformanceReview() {
				this.isLoadingPerformanceReview = true;
				try {
					this.performanceReview = await this.$store.dispatch('performanceReviews/getById', this.id);
				} catch (err) {
				} finally {
					this.isLoadingPerformanceReview = false;
				}
			},
			downloadFile(id, fileName) {
				this.$store.dispatch('performanceReviews/downloadDocument', {
					performanceReviewId: this.id,
					id: id,
					fileName: fileName
				});
			},
			editReview() {
				this.$router.push(`/pr/${this.id}/edit`);
			},
			viewDiscussion() {
				this.$router.push(`/pr/${this.id}/discussion`);
			},
			onEditScheduledDateClick() {
				this.scheduleDialog = true;
			},
			onEditMeetingLocationClick() {
				this.scheduleDialog = true;
			},
			onBackClick() {
				window.history.back();
			},
			onReschedule(obj) {
				this.isSavingReschedule = true;
				this.$store.dispatch('performanceReviews/updateSchedule', obj).then(
					response => {
						// this.$router.push(`/pr/${response.data.id}`)
						this.updatedNotifText = 'Performance Review Rescheduled';
						this.updatedNotif = true;
						this.scheduleDialog = false;
						this.isSavingReschedule = false;
					},
					error => {
						this.isSavingReschedule = false;
						console.error('Error rescheduling performance review');
					}
				);
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			}
		}
	};
</script>

<style lang="scss">
	@import '@/styles/_colors.scss';

	.pr-overdue {
		color: $red;
	}
</style>
